import { List, X } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LevelIcon } from '@/components/events/LevelBand'
import { useEventTitle } from '@/hooks/useEventTitle'
import { useClueReasons, useMarkSeen } from '@/hooks/useSuspiciousEvents'
import { eventOpenTarget } from '@/lib/navigation'
import { showInList } from '@/lib/alertRowLink'
import { useAlertLinkHandlers } from '@/store/alertLinkStore'
import type { DetectionNotification } from './shared'

// #413 — the Suspicious toast: stays until someone presses Seen on the event (NotificationProvider closes it) or this
// tab closes it with the X (the event stays open in the list). "Mark seen" sends Seen and Read, then closes it.
export function SuspiciousToast({ notif, onClose }: { notif: DetectionNotification; onClose: (id: string) => void }) {
  const { t } = useTranslation('common')
  const clueReasons = useClueReasons()
  const eventTitle = useEventTitle()
  const markSeen = useMarkSeen()
  const eventId = (notif.event as { id?: unknown } | null)?.id
  const navigate = useNavigate()
  const location = useLocation()
  const link = useAlertLinkHandlers(typeof eventId === 'number' ? eventId : undefined)
  const reasons = clueReasons(notif.event).join(', ')
  const title = eventTitle({ ...notif.event, event_type: notif.eventType }, notif.title)
  const camera = notif.camera ?? ''

  return (
    <section role="group" className="sus-toast" aria-label={t('suspicious.toast.label', { camera })} {...link}>
      <p className="sus-toast-head">
        <LevelIcon level="suspicious" />
        <b>{t('suspicious.label')}</b>
        {reasons ? ` · ${reasons}` : null}
      </p>
      <div className="sus-toast-body">
        {notif.thumbnailUrl && (
          <img
            className="sus-toast-thumb"
            src={notif.thumbnailUrl}
            alt=""
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        )}
        <div className="sus-toast-text">
          <p className="sus-toast-camera">{camera}</p>
          <p className="sus-toast-meta">{[notif.timestamp, title].filter(Boolean).join(' · ')}</p>
        </div>
      </div>
      <div className="sus-toast-actions">
        <button
          type="button"
          className="sus-seen sus-toast-seen"
          disabled={markSeen.isPending || typeof eventId !== 'number'}
          onClick={() => {
            if (typeof eventId === 'number') markSeen.mutate(eventId, { onSuccess: () => onClose(notif.id) })
          }}
        >
          {t('suspicious.toast.markSeen')}
        </button>
        <button
          type="button"
          className="sus-toast-show alert-show-in-list"
          disabled={typeof eventId !== 'number'}
          onClick={(e) => {
            if (typeof eventId !== 'number') return
            showInList(eventId, () => { const { to, options } = eventOpenTarget(location, eventId); navigate(to, options) }, { focus: e.detail === 0 })
          }}
        >
          <List size={14} aria-hidden />
          {t('alertLink.showInList')}
        </button>
        <button type="button" className="sus-toast-x" aria-label={t('suspicious.toast.close')} onClick={() => onClose(notif.id)}>
          <X size={16} aria-hidden />
        </button>
      </div>
    </section>
  )
}
